import {
  forwardRef,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dropdown, Spinner } from "react-bootstrap";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useAuthStore } from "src/store/authStore";
import { useLogout } from "src/hooks/useAuth";
import { usePortalMe } from "src/hooks/usePortal";
import { usePortalAuthStore } from "src/store/portalAuthStore";
import { getDashboardsApi, setDashboardDefaultApi } from "src/api/dashboardApi";
import { DashboardListResponse } from "src/types/dashboard";
import useBuilderStore from "src/store/builderStore";
import { SiteLogo } from "../../assets";

type ToggleButtonProps = {
  children: ReactNode;
  className?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
};

const UserMenuToggle = forwardRef<HTMLButtonElement, ToggleButtonProps>(
  ({ children, className, onClick }, ref) => (
    <button
      ref={ref}
      type="button"
      className={className}
      onClick={(event) => {
        event.preventDefault();
        onClick?.(event);
      }}
    >
      {children}
    </button>
  ),
);

UserMenuToggle.displayName = "UserMenuToggle";

export default function BuilderHeader() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const navigate = useNavigate();
  const { user: userData, refreshToken } = useAuthStore();
  const portalToken = usePortalAuthStore((s) => s.token);
  const { data: portalMe } = usePortalMe(!!portalToken);
  const isSuperAdmin = portalMe?.role === "super_admin";
  const { selectedDashboardCode, setSelectedDashboardCode } = useBuilderStore();
  const { mutate: logout, isPending: isLoggingOut } = useLogout();
  const queryClient = useQueryClient();

  const { data: allDashboards } = useQuery<DashboardListResponse, Error>({
    queryKey: ["dashboards"],
    queryFn: () => getDashboardsApi(),
    enabled: !!userData,
    staleTime: 0,
  });

  useEffect(() => {
    const foundDashboard = allDashboards?.dashboards?.find(
      (dash) => dash.is_default === true,
    );

    if (foundDashboard && !selectedDashboardCode) {
      setSelectedDashboardCode(foundDashboard.code);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDashboards]);

  const { mutate: setDefault } = useMutation({
    mutationFn: (code: string) => setDashboardDefaultApi(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboards"] });
      toast.success("Default dashboard updated.");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to set default dashboard.");
    },
  });

  return (
    <nav className="navbar navbar-expand-lg auth-header">
      <div className="container-fluid container-auth px-5">
        <Link className="navbar-brand text-decoration-none" to={"/dashboards"}>
          <img src={SiteLogo} alt="Site logo" width={150} />
        </Link>
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#authHeader"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse ms-3" id="authHeader">
          <ul className="navbar-nav me-auto dashboard-tabs">
            {allDashboards?.default?.map((section) => {
              const hasActiveDashboard = section.dashboards.some(
                (d) => selectedDashboardCode === d.code,
              );
              return (
                <Dropdown
                  key={section.code ?? section.type}
                  as="li"
                  className="nav-item"
                >
                  <Dropdown.Toggle
                    as={UserMenuToggle}
                    id={`builder-section-${section.code ?? section.type}`}
                    className={`nav-link dashboard-tab${hasActiveDashboard ? " custom-active-link" : ""}`}
                  >
                    {section.name}
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    popperConfig={{
                      modifiers: [
                        {
                          name: "offset",
                          options: { offset: [0, 12] },
                        },
                      ],
                    }}
                  >
                    {section.dashboards.map((dashboard) => (
                      <Dropdown.Item
                        key={dashboard.code}
                        as="button"
                        active={selectedDashboardCode === dashboard.code}
                        className="d-flex align-items-center justify-content-between"
                        onClick={() => {
                          setSelectedDashboardCode(dashboard.code);
                          navigate(`/builder/${dashboard.code}`);
                        }}
                      >
                        {dashboard.name}
                        <i
                          className={`bi ms-2 ${dashboard.is_default ? "bi-star-fill text-warning" : "bi-star"}`}
                          role="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!dashboard.is_default)
                              setDefault(dashboard.code);
                          }}
                        />
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              );
            })}
            {allDashboards?.my_dashboards &&
              allDashboards.my_dashboards.length > 0 && (
                <Dropdown as="li" className="nav-item">
                  <Dropdown.Toggle
                    as={UserMenuToggle}
                    id="builder-section-my-dashboards"
                    className={`nav-link dashboard-tab${
                      allDashboards.my_dashboards.some(
                        (d) => selectedDashboardCode === d.code,
                      )
                        ? " custom-active-link"
                        : ""
                    }`}
                  >
                    My Dashboards
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    popperConfig={{
                      modifiers: [
                        {
                          name: "offset",
                          options: { offset: [0, 12] },
                        },
                      ],
                    }}
                  >
                    {allDashboards.my_dashboards.map((dashboard) => (
                      <Dropdown.Item
                        key={dashboard.code}
                        as="button"
                        active={selectedDashboardCode === dashboard.code}
                        className="d-flex align-items-center justify-content-between"
                        onClick={() => {
                          setSelectedDashboardCode(dashboard.code);
                          navigate(`/builder/${dashboard.code}`);
                        }}
                      >
                        {dashboard.name}
                        <i
                          className={`bi ms-2 ${dashboard.is_default ? "bi-star-fill text-warning" : "bi-star"}`}
                          role="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!dashboard.is_default)
                              setDefault(dashboard.code);
                          }}
                        />
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              )}
          </ul>

          <ul className="navbar-nav justify-content-end">
            {!userData ? (
              <div className="placeholder-glow">
                <span
                  className="placeholder"
                  style={{ width: "160px", height: "30px" }}
                ></span>
              </div>
            ) : (
              <Dropdown
                as="li"
                className="nav-item"
                show={isUserMenuOpen}
                onToggle={(nextShow) => setIsUserMenuOpen(nextShow)}
              >
                <Dropdown.Toggle
                  id="user-dropdown"
                  as={UserMenuToggle}
                  className={`nav-link ${
                    isUserMenuOpen ? "custom-active-link" : ""
                  }`}
                >
                  {[userData?.firstname, userData?.lastname]
                    .filter(Boolean)
                    .join(" ") || userData?.name}
                </Dropdown.Toggle>

                <Dropdown.Menu
                  popperConfig={{
                    modifiers: [
                      {
                        name: "offset",
                        options: { offset: [0, 12] },
                      },
                    ],
                  }}
                >
                  <Dropdown.Item
                    onClick={() =>
                      navigate(
                        selectedDashboardCode
                          ? `/dashboards/${selectedDashboardCode}`
                          : "/dashboards",
                      )
                    }
                    as="button"
                  >
                    Active Dashboards
                  </Dropdown.Item>
                  {isSuperAdmin && (
                    <>
                      <Dropdown.Divider />
                      <Dropdown.Header>Admin Portal</Dropdown.Header>
                      <Dropdown.Item as={Link} to="/portal/dashboards">
                        Dashboards
                      </Dropdown.Item>
                      <Dropdown.Item as={Link} to="/portal/users">
                        Users
                      </Dropdown.Item>
                      <Dropdown.Item as={Link} to="/portal/clients">
                        Clients
                      </Dropdown.Item>
                      <Dropdown.Item as={Link} to="/portal/programs">
                        Programs
                      </Dropdown.Item>
                      <Dropdown.Item as={Link} to="/portal/tiers">
                        Tiers
                      </Dropdown.Item>
                    </>
                  )}
                  <Dropdown.Divider />
                  <Dropdown.Item
                    as="button"
                    onClick={() => logout(refreshToken || "")}
                    disabled={isLoggingOut}
                  >
                    {isLoggingOut ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-1"
                        />{" "}
                        Signing out…
                      </>
                    ) : (
                      "Logout"
                    )}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
}
